import React, { useMemo, useState } from 'react';
import { useQuery, NetworkStatus, useParams } from '@common/hooks';
import { ErrorHandler, LoadingState, Tabs, History } from '@common/components';
import { SHIFT_DETAILS_QUERY } from '@scheduling/graphql';
import {
  ShiftRequestDetailsContent,
  ShiftOpeningsDrawerTable,
  MatchBreakdownDrawerTable,
} from '@scheduling/components';
import { ShiftRequestEditContent } from './_shift-request-edit-content';
import { Empty, FormInstance } from 'antd';
import { ShiftMessages } from '@scheduling/components/shift-messages';
import { useCurrentMembership } from '@identity/hooks';

const ARCHIVED_OPENING_STATES = ['cancelled', 'expired'];

export function ShiftRequestDetailsBody({
  shiftId,
  matchBreakdownEditMode,
  setMatchBreakdownEditMode,
  wfgOverrideRates,
  setWfgOverrideRates,
  shiftEditMode,
  setShiftEditMode,
  shiftGapEditForm,
  canEditShiftDetails,
  canEditRates,
  tagsEnabled,
  canSendShift,
}: {
  shiftId: string;
  matchBreakdownEditMode: boolean;
  setMatchBreakdownEditMode: (value: boolean) => void;
  wfgOverrideRates: {
    workforceGroupId: string;
    rate: number;
    previousRate: number;
  }[];
  setWfgOverrideRates: (
    value: { workforceGroupId: string; rate: number; previousRate: number }[]
  ) => void;
  shiftEditMode: boolean;
  setShiftEditMode: (value: boolean) => void;
  shiftGapEditForm: FormInstance;
  canEditShiftDetails: boolean;
  canEditRates: boolean;
  tagsEnabled: boolean;
  canSendShift: boolean;
}) {
  const { type } = useParams<{ type: string }>();
  const [tab, setTab] = useState('shifts');

  const { data, loading, networkStatus, error } = useQuery(
    SHIFT_DETAILS_QUERY,
    {
      variables: { id: shiftId, sourcePage: type },
    }
  );
  const currentMembership = useCurrentMembership();

  const shift = data?.shiftGap;

  const notArchivedOpenings = useMemo(() => {
    const worksShiftSortStates = [
      'accepted',
      'confirmed',
      'cancelled',
      'rejected',
    ];
    return (
      (shift?.shiftGapOpenings || [])
        .filter((shiftGapOpening) =>
          ARCHIVED_OPENING_STATES.every(
            (state) => shiftGapOpening?.state !== state
          )
        )
        .sort((a, b) => {
          if (a.canApproveReject?.value && a.worksShift) {
            return -1;
          }
          if (a.worksShift?.state && b.worksShift?.state) {
            return (
              worksShiftSortStates.indexOf(a.worksShift.state) -
              worksShiftSortStates.indexOf(b.worksShift.state)
            );
          }
          if (a.worksShift?.state) {
            return -1;
          }
          return 1;
        }) || []
    );
  }, [shift]);

  const displayMatchBreakdown =
    shift && !['filled', 'expired'].includes(shift?.state);

  const openOpeningsCount = useMemo(
    () =>
      notArchivedOpenings.filter(
        (shiftGapOpening) => shiftGapOpening.state === 'open'
      ).length,
    [notArchivedOpenings]
  );

  if ((loading && networkStatus !== NetworkStatus.refetch) || !shift)
    return <LoadingState />;
  if (error)
    return <ErrorHandler error={error} log="ShiftRequestDetailsBody" />;

  const canAddOpenings = shift ? shift.canAddOpenings.value : false;

  const tabItems = [
    {
      key: 'shifts',
      label: 'Shifts',
      children: (
        <ShiftOpeningsDrawerTable
          shiftGapOpenings={notArchivedOpenings}
          count={openOpeningsCount}
          shift={shift}
          canAddOpenings={canAddOpenings}
          worksUser={currentMembership.worksUser}
          loading={loading}
        />
      ),
    },
    {
      key: 'rates',
      label: 'Rates',
      disabled: !displayMatchBreakdown,
      children: (
        <MatchBreakdownDrawerTable
          unitId={shift.organizationUnit.id}
          shiftId={shift.id}
          matchBreakdownEditMode={matchBreakdownEditMode}
          setMatchBreakdownEditMode={setMatchBreakdownEditMode}
          wfgOverrideRates={wfgOverrideRates}
          setWfgOverrideRates={setWfgOverrideRates}
          shiftEditMode={shiftEditMode}
          canEditRates={canEditRates}
        />
      ),
    },
    ...(canSendShift
      ? [
          {
            key: 'messages',
            label: 'Messages',
            children: (
              <ShiftMessages
                hideShiftDetail
                hideColumns={['role', 'unit', 'duration', 'shiftStartDate']}
                shiftGapId={shift.id}
                emptyText={
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No messages"
                  />
                }
              />
            ),
          },
        ]
      : []),
    {
      key: 'history',
      label: 'History',
      children: (
        <div style={{ marginTop: 24 }}>
          <History data={shift.history || []} />
        </div>
      ),
    },
  ];

  return (
    <>
      {shiftEditMode ? (
        <ShiftRequestEditContent
          shift={shift}
          setShiftEditMode={setShiftEditMode}
          shiftGapEditForm={shiftGapEditForm}
          canEditShiftDetails={canEditShiftDetails}
          tagsEnabled={tagsEnabled}
          worksUser={currentMembership.worksUser}
        />
      ) : (
        <ShiftRequestDetailsContent
          shift={shift}
          setShiftEditMode={setShiftEditMode}
          matchBreakdownEditMode={matchBreakdownEditMode}
          canEditShiftDetails={canEditShiftDetails}
          tagsEnabled={tagsEnabled}
          showOpeningCount
        />
      )}
      <Tabs
        className="ShiftDetails"
        onChange={setTab}
        activeKey={tab}
        containerStyle={{
          height: 'auto',
          marginTop: 24,
        }}
        items={tabItems}
      />
    </>
  );
}
